import { useEffect, useState } from "react";
import api, { API_BASE, formatMoney } from "@/lib/api";
import { X, Download, Receipt, User, Buildings, Bank, IdentificationCard } from "@phosphor-icons/react";

/**
 * Corporate payslip breakdown drawer (RazorpayX / Zoho People style).
 * Shows employee info, attendance, two-column earnings/deductions,
 * net pay grand total, leave balance, and a download-PDF CTA.
 */
export default function PayslipDetailDrawer({ payslip, onClose }) {
  const [leaveBal, setLeaveBal] = useState(null);

  useEffect(() => {
    if (!payslip) return;
    // Best-effort leave-balance fetch. Falls back to zeros if not staff/admin.
    api.get(`/payroll/me/leave-balance?period=${payslip.period}`)
      .then(r => setLeaveBal(r.data))
      .catch(() => setLeaveBal(null));
  }, [payslip]);

  if (!payslip) return null;
  const earnings = payslip.earnings || {};
  const deductions = payslip.deductions || {};
  const currency = payslip.currency || "INR";
  const period = prettyPeriod(payslip.period);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="payslip-drawer">
      <div
        onClick={e => e.stopPropagation()}
        className="bg-paper w-full max-w-2xl h-full overflow-y-auto shadow-2xl"
      >
        {/* Sticky header band */}
        <div className="sticky top-0 z-10 bg-ink text-white px-6 py-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-mustard">Salary Payslip</div>
            <div className="font-heading font-bold text-2xl">{period}</div>
            <div className="text-xs text-white/70 mt-0.5">
              {payslip.staff_name} · {payslip.designation || payslip.category}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`${API_BASE}/payroll/payslips/${payslip.payslip_id}/pdf`}
              target="_blank" rel="noreferrer"
              className="px-3 py-2 rounded-full bg-mustard text-ink text-sm font-semibold inline-flex items-center gap-1.5"
              data-testid="payslip-drawer-pdf"
            >
              <Download size={14} weight="bold" /> PDF
            </a>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" data-testid="payslip-drawer-close">
              <X size={20} weight="bold" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Employee block */}
          <div className="grid grid-cols-2 gap-3 bg-white border border-border rounded-2xl p-4 text-sm">
            <KV icon={User} label="Employee" value={payslip.staff_name} />
            <KV icon={IdentificationCard} label="Employee ID" value={payslip.employee_code || payslip.staff_id?.slice(-8).toUpperCase()} />
            <KV icon={Buildings} label="Department" value={payslip.department || payslip.category || "—"} />
            <KV icon={Receipt} label="Designation" value={payslip.designation || "—"} />
          </div>

          {/* Attendance band */}
          <div className="grid grid-cols-5 gap-2">
            {[
              ["Working days", payslip.working_days],
              ["Present", payslip.present_days],
              ["Paid leave", payslip.paid_leave_days || payslip.leave_days || 0],
              ["LOP", payslip.lop_days || payslip.absent_days || 0],
              ["Payable", (payslip.working_days || 0) - (payslip.lop_days || payslip.absent_days || 0)],
            ].map(([k, v]) => (
              <div key={k} className="bg-terracotta-soft/40 border border-terracotta-soft rounded-2xl py-3 px-2 text-center">
                <div className="text-xl font-bold text-terracotta-dark">{v ?? 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-ink-soft mt-1">{k}</div>
              </div>
            ))}
          </div>

          {/* Earnings + Deductions */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-border rounded-2xl overflow-hidden">
              <div className="bg-terracotta text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider">Earnings</div>
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(earnings)
                    .filter(([k, v]) => v || k === "Basic")
                    .map(([k, v]) => (
                    <tr key={k} className="border-t border-border first:border-t-0">
                      <td className="px-4 py-2 text-ink-soft">{k}</td>
                      <td className="px-4 py-2 text-right font-medium">{formatMoney(v, currency)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-ink bg-mustard-soft/40">
                    <td className="px-4 py-2.5 font-bold">Gross Earnings (A)</td>
                    <td className="px-4 py-2.5 text-right font-bold">{formatMoney(payslip.gross, currency)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-border rounded-2xl overflow-hidden">
              <div className="bg-lavender-deep text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider">Deductions</div>
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(deductions).map(([k, v]) => (
                    <tr key={k} className="border-t border-border first:border-t-0">
                      <td className="px-4 py-2 text-ink-soft">{k}</td>
                      <td className="px-4 py-2 text-right font-medium">{formatMoney(v, currency)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-ink bg-mustard-soft/40">
                    <td className="px-4 py-2.5 font-bold">Total Deductions (B)</td>
                    <td className="px-4 py-2.5 text-right font-bold">{formatMoney(payslip.deductions_total, currency)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Net pay grand band */}
          <div className="bg-ink text-white rounded-2xl p-5 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-mustard">Net Payable (A − B)</div>
              <div className="text-3xl font-bold mt-1">{formatMoney(payslip.net_payable, currency)}</div>
            </div>
            <div className="text-right text-xs text-white/60 max-w-[60%]">
              Employer PF contribution: {formatMoney(payslip.employer_pf || 0, currency)} · This payslip is system generated.
            </div>
          </div>

          {/* Leave balance */}
          {leaveBal && (
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-soft font-semibold mb-2 flex items-center gap-1.5">
                <Bank size={12} weight="bold" /> Leave balance (remaining)
              </div>
              <div className="grid grid-cols-5 gap-2">
                {["sick", "casual", "earned", "compensatory", "unpaid"].map(k => (
                  <div key={k} className="bg-white border border-border rounded-2xl py-2.5 text-center">
                    <div className="text-lg font-bold text-ink">{leaveBal[k] ?? "—"}</div>
                    <div className="text-[10px] uppercase tracking-wider text-ink-soft capitalize">{k}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      {Icon ? <Icon size={14} weight="duotone" className="text-mango mt-0.5 shrink-0" /> : null}
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-ink-soft">{label}</div>
        <div className="font-medium text-sm truncate">{value || "—"}</div>
      </div>
    </div>
  );
}

function prettyPeriod(p) {
  if (!p) return "—";
  try {
    const [y, m] = p.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  } catch { return p; }
}
